import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { useWorkspaceCredits } from './useWorkspaceCredits';
import { toast } from 'sonner';
import { addDays, subDays, startOfDay, endOfDay } from 'date-fns';

export interface UsageAnalytics {
  id: string;
  workspace_id: string;
  period_start: string;
  period_end: string;
  total_credits_consumed: number;
  average_daily_usage: number;
  peak_usage_hour: number | null;
  peak_usage_day: number | null;
  usage_efficiency_score: number | null;
  cost_optimization_potential: number | null;
  service_type_breakdown: any;
  predicted_monthly_usage: number | null;
  anomaly_score: number | null;
  trend_direction: 'increasing' | 'decreasing' | 'stable' | null;
  created_at: string;
}

export interface SmartAlert {
  id: string;
  workspace_id: string;
  alert_type: string;
  severity: string;
  title: string;
  message: string;
  recommendation: any;
  trigger_data: any;
  is_read: boolean | null;
  is_dismissed: boolean | null;
  action_taken: boolean | null;
  expires_at: string | null;
  created_at: string;
}

export interface ConversionOpportunity {
  id: string;
  workspace_id: string;
  opportunity_type: string;
  trigger_usage_threshold: number | null;
  current_usage: number | null;
  potential_savings: number | null;
  roi_estimate: number | null;
  urgency_score: number | null;
  presentation_data: any;
  is_active: boolean | null;
  user_response: string | null;
  created_at: string;
}

export interface ServiceAnalytics {
  id: string;
  workspace_id: string;
  service_type: string;
  date: string;
  usage_count: number | null;
  credits_consumed: number | null;
  average_cost_per_operation: number | null;
  efficiency_score: number | null;
  error_rate: number | null;
  processing_time_avg_ms: number | null;
  optimization_suggestions: any;
}

export const useEnhancedCredits = () => {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const { creditWallet, transactions } = useWorkspaceCredits();

  // Usage Analytics
  const {
    data: usageAnalytics,
    isLoading: isLoadingAnalytics
  } = useQuery({
    queryKey: ['credit-usage-analytics', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('credit_usage_analytics')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false })
        .limit(30);
      
      if (error) throw error;
      return data as UsageAnalytics[];
    },
    enabled: !!currentWorkspace?.id
  });

  // Smart Alerts
  const {
    data: smartAlerts,
    isLoading: isLoadingAlerts
  } = useQuery({
    queryKey: ['credit-smart-alerts', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('credit_smart_alerts')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .eq('is_dismissed', false)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as SmartAlert[];
    },
    enabled: !!currentWorkspace?.id
  });

  // Conversion Opportunities
  const {
    data: conversionOpportunities,
    isLoading: isLoadingOpportunities
  } = useQuery({
    queryKey: ['credit-conversion-opportunities', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('credit_conversion_opportunities')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .eq('is_active', true)
        .order('urgency_score', { ascending: false });
      
      if (error) throw error;
      return data as ConversionOpportunity[];
    },
    enabled: !!currentWorkspace?.id
  });

  // Service Analytics
  const {
    data: serviceAnalytics,
    isLoading: isLoadingServices
  } = useQuery({
    queryKey: ['credit-service-analytics', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('credit_service_analytics')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .gte('date', subDays(new Date(), 30).toISOString().split('T')[0])
        .order('date', { ascending: false });
      
      if (error) throw error;
      return data as ServiceAnalytics[];
    },
    enabled: !!currentWorkspace?.id
  });

  // Mark alert as read
  const markAlertAsRead = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from('credit_smart_alerts')
        .update({ is_read: true })
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-smart-alerts'] });
    }
  });

  // Dismiss alert
  const dismissAlert = useMutation({
    mutationFn: async (alertId: string) => {
      const { error } = await supabase
        .from('credit_smart_alerts')
        .update({ is_dismissed: true })
        .eq('id', alertId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-smart-alerts'] });
      toast.success('Alert dismissed');
    }
  });

  // Respond to conversion opportunity
  const respondToOpportunity = useMutation({
    mutationFn: async ({ 
      opportunityId, 
      response 
    }: { 
      opportunityId: string; 
      response: 'interested' | 'dismissed' | 'converted' | 'postponed';
    }) => {
      const { error } = await supabase
        .from('credit_conversion_opportunities')
        .update({ 
          user_response: response,
          is_active: response !== 'dismissed' && response !== 'converted'
        })
        .eq('id', opportunityId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-conversion-opportunities'] });
      toast.success('Response recorded');
    }
  });

  // Generate usage forecast
  const generateForecast = () => {
    if (!transactions || transactions.length < 7) {
      return null;
    }

    const recentTransactions = transactions
      .filter(t => t.transaction_type === 'usage_debit')
      .slice(0, 14);

    const dailyUsage = recentTransactions.reduce((sum, t) => sum + Math.abs(t.amount), 0) / 14;
    const weeklyUsage = dailyUsage * 7;
    const monthlyUsage = dailyUsage * 30;

    return {
      daily: Math.round(dailyUsage * 100) / 100,
      weekly: Math.round(weeklyUsage * 100) / 100,
      monthly: Math.round(monthlyUsage * 100) / 100,
      daysRemaining: creditWallet ? Math.floor(creditWallet.credits_balance / dailyUsage) : 0
    };
  };

  // Calculate efficiency metrics
  const calculateEfficiency = () => {
    if (!serviceAnalytics || serviceAnalytics.length === 0) {
      return { score: 0, errorRate: 0, suggestions: [] };
    }

    const avgEfficiency = serviceAnalytics.reduce((sum, s) => sum + (s.efficiency_score || 0), 0) / serviceAnalytics.length;
    const totalErrorRate = serviceAnalytics.reduce((sum, s) => sum + (s.error_rate || 0), 0) / serviceAnalytics.length;

    const suggestions = [];
    if (totalErrorRate > 5) {
      suggestions.push('Reduce error rates to improve cost efficiency');
    }
    if (avgEfficiency < 70) {
      suggestions.push('Consider optimizing service usage patterns');
    }

    return {
      score: Math.round(avgEfficiency),
      errorRate: Math.round(totalErrorRate * 100) / 100,
      suggestions
    };
  };

  // Get usage insights
  const getUsageInsights = () => {
    const forecast = generateForecast();
    const efficiency = calculateEfficiency();
    const currentAnalytics = usageAnalytics?.[0];

    return {
      forecast,
      efficiency,
      trend: currentAnalytics?.trend_direction,
      anomalyScore: currentAnalytics?.anomaly_score || 0,
      optimizationPotential: currentAnalytics?.cost_optimization_potential || 0,
      peakUsageHour: currentAnalytics?.peak_usage_hour,
      peakUsageDay: currentAnalytics?.peak_usage_day
    };
  };

  return {
    // Data
    usageAnalytics,
    smartAlerts,
    conversionOpportunities,
    serviceAnalytics,
    
    // Loading states
    isLoadingAnalytics,
    isLoadingAlerts,
    isLoadingOpportunities,
    isLoadingServices,
    
    // Actions
    markAlertAsRead,
    dismissAlert,
    respondToOpportunity,
    
    // Computed values
    insights: getUsageInsights(),
    
    // Utility functions
    generateForecast,
    calculateEfficiency
  };
};